import { IconPencil } from "@tabler/icons-react";
import React from "react";
import { toast } from "sonner";

import { Loader } from "@/components/Loader";
import { useTeam } from "@/components/providers/team";
import { useFetchTeamMembers } from "@/components/queries/team";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";

export function MemberNoteDialog({
	row,
	memberID,
	disabled,
}: {
	row: string;
	memberID: string;
	disabled: boolean;
}) {
	const { teamID } = useTeam();
	const [isLoading, setIsLoading] = React.useState(false);
	const [notes, setNotes] = React.useState(row);
	const { refetchTeamMembers } = useFetchTeamMembers(teamID as string);

	return (
		<AlertDialog>
			<AlertDialogTrigger
				className={buttonVariants({
					variant: "outline",
					size: "icon-sm",
				})}
				aria-label="Edit member notes"
				disabled={disabled}
			>
				<IconPencil className="h-4 w-4" />
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Member Notes</AlertDialogTitle>
					<AlertDialogDescription>
						Add or edit notes for this team member.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<textarea
					className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
					value={notes || ""}
					onChange={(e) => setNotes(e.target.value)}
					placeholder="Enter notes here..."
				/>
				<AlertDialogFooter>
					<AlertDialogCancel>Cancel</AlertDialogCancel>
					<AlertDialogAction
						onClick={async () => {
							if (disabled) return;
							setIsLoading(true);
							try {
								await requestWithHandling<void>(() =>
									apiClient.api.teamMemberUpdate(teamID as string, {
										id: memberID,
										notes,
									}),
								);
								toast.success("Notes updated successfully");
								await refetchTeamMembers();
							} catch (error) {
								toast.error("Failed to update notes", {
									description:
										error instanceof TypedClientApiError
											? error.message
											: "An unknown error occurred",
								});
							} finally {
								setIsLoading(false);
							}
						}}
						disabled={isLoading || disabled}
					>
						{isLoading ? <Loader /> : "Save"}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
